import React from "react";
import { LOGO_URL } from "../lib/pdf";
import {
  NSCA_ROWS,
  RPE_SCALE,
  computeLoadout,
  fmt,
  roundTo,
  rpeForPct,
  toNum,
} from "../lib/calc";

// Fiche imprimée — optimisée pour une impression noir et blanc
export const PrintSheet = ({
  exercise,
  oneRM,
  unit,
  rounding,
  selectedPct,
  plateCfg,
}) => {
  const rm = toNum(oneRM);
  const target = (rm * (selectedPct || 100)) / 100;
  const load = computeLoadout({
    target,
    targetUnit: unit,
    profileKey: plateCfg?.profileKey,
    bar: plateCfg?.bar,
    collars: plateCfg?.collars,
  });

  return (
    <div className="print-only ps" data-testid="print-header">
      <div className="ps-head">
        <img src={LOGO_URL} alt="Kraken PP" className="ps-logo" />
        <div>
          <div className="ps-brand">KRAKEN PP</div>
          <div className="ps-sub">Préparation physique · krakenpp.ca</div>
        </div>
        <div className="ps-head-right">
          <div className="ps-title">Fiche de charges</div>
          <div className="ps-sub">{new Date().toLocaleDateString("fr-CA")}</div>
        </div>
      </div>

      <div className="ps-rule" />

      <div className="ps-info">
        <div className="ps-field">
          <span className="ps-label">Athlète</span>
          <span className="ps-value ps-fill" />
        </div>
        <div className="ps-field">
          <span className="ps-label">Exercice</span>
          <span className="ps-value">{exercise}</span>
        </div>
        <div className="ps-field">
          <span className="ps-label">1RM</span>
          <span className="ps-value ps-big">
            {fmt(rm, 1)} {unit}
          </span>
        </div>
        <div className="ps-field">
          <span className="ps-label">Arrondi</span>
          <span className="ps-value">
            {rounding ? `${rounding} ${unit}` : "exact"}
          </span>
        </div>
        <div className="ps-field">
          <span className="ps-label">Date</span>
          <span className="ps-value ps-fill" />
        </div>
      </div>

      <table className="ps-table">
        <thead>
          <tr>
            <th className="ps-c">% 1RM</th>
            <th className="ps-c">Reps max</th>
            <th className="ps-c">RPE / RIR</th>
            <th className="ps-r">Charge ({unit})</th>
            <th>Séries réalisées / notes</th>
          </tr>
        </thead>
        <tbody>
          {NSCA_ROWS.map((row) => {
            const l = roundTo((rm * row.pct) / 100, rounding);
            const r = rpeForPct(row.pct);
            const active = row.pct === selectedPct;
            return (
              <tr key={row.pct} className={active ? "ps-active" : ""}>
                <td className="ps-c ps-b">
                  {row.pct}%{active ? " ◀" : ""}
                </td>
                <td className="ps-c">{row.reps} RM</td>
                <td className="ps-c">
                  {r.rpe} <span className="ps-muted">({r.rir} RIR)</span>
                </td>
                <td className="ps-r ps-b ps-big">{fmt(l, rounding ? 0 : 1)}</td>
                <td className="ps-notes" />
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="ps-cols">
        <div className="ps-box">
          <div className="ps-box-title">
            Chargement de la barre · {selectedPct}% ={" "}
            {fmt(load.total, 2)} {load.unit}
          </div>
          <div className="ps-sub ps-mb">
            {load.profile.label} · barre {load.bar} {load.unit}
            {load.collarWeight
              ? ` + colliers ${load.collarWeight} ${load.unit} / côté`
              : " · sans colliers"}
          </div>
          <table className="ps-table ps-table-sm">
            <thead>
              <tr>
                <th>Disque</th>
                <th className="ps-c">Par côté</th>
              </tr>
            </thead>
            <tbody>
              {load.perSide.length === 0 ? (
                <tr>
                  <td colSpan={2}>Barre seule</td>
                </tr>
              ) : (
                load.perSide.map(({ plate, count }) => (
                  <tr key={plate}>
                    <td className="ps-b">
                      {plate} {load.unit}
                    </td>
                    <td className="ps-c ps-b">× {count}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {load.remainder > 0.01 && (
            <div className="ps-sub ps-mt">
              Écart par côté : {fmt(load.remainder, 2)} {load.unit}
            </div>
          )}
        </div>

        <div className="ps-box">
          <div className="ps-box-title">
            Barème RPE / RIR — RTS (Tuchscherer)
          </div>
          <table className="ps-table ps-table-sm">
            <thead>
              <tr>
                <th className="ps-c">RPE</th>
                <th className="ps-c">RIR</th>
                <th>Sensation</th>
              </tr>
            </thead>
            <tbody>
              {RPE_SCALE.map((s) => (
                <tr key={s.rpe}>
                  <td className="ps-c ps-b">{s.rpe}</td>
                  <td className="ps-c">{s.rir}</td>
                  <td>{s.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ps-foot">
        Kraken Préparation Physique · krakenpp.ca · RPE 10 = 0 rep en réserve
      </div>
    </div>
  );
};
